import { useEffect, useMemo, useState } from "react";

import TabBar from "../../components/TabBar";
import TransactionComponent from "../../components/TransactionComponent";
import TransactionHeader from "../../components/TransactionHeader";
import DownloadWallet from "../../components/DownloadWallet";
import Directory from "../../components/Directory";
import HeaderSection from "../../components/HeaderSection";
import NoData from "../../components/NoData";
import { api } from "../../api/client";
import { mapWillForUi } from "../../utils/icons";
import { ConfigProvider, DatePicker, theme } from 'antd';

const { RangePicker } = DatePicker;
const dateFormat = 'YYYY/MM/DD';

export default function MainScene() {
    const [tabBar, setTabBar] = useState(0);
    const [stats, setStats] = useState({ all: 0, testament: 0, to_claim: 0, claimed: 0 });
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [range, setRange] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { darkAlgorithm } = theme;

    const statusFilter = useMemo(() => {
        if (tabBar === 1) return null; // all testaments
        if (tabBar === 2) return 'to_claim';
        if (tabBar === 3) return 'claimed';
        return null;
    }, [tabBar]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            setError('');
            try {
                const [statsData, willsData] = await Promise.all([
                    api.stats(),
                    api.getWills({
                        status: statusFilter || undefined,
                        q: search || undefined,
                        from: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
                        to: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
                        scope: 'directory',
                    }),
                ]);
                if (cancelled) return;
                setStats(statsData);
                let list = willsData.items || [];
                if (tabBar === 1) {
                    list = list.filter((item) => item.type === 'Testament');
                }
                setItems(list.map(mapWillForUi));
            } catch (err) {
                if (!cancelled) {
                    setError(err.message);
                    setItems([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [tabBar, search, range, statusFilter]);

    return (
        <div className="bg-[#121212] min-w-0">
            <HeaderSection account={false} />
            <Directory value={search} onSearch={setSearch} />
            <div className="w-full min-w-0">
                <div className="bg-[#121212] w-full px-4 sm:px-[20px] lg:px-[140px] pt-8 sm:pt-[60px]">
                    <div className="bg-[#1B1B1B] rounded-[16px] w-full px-4 sm:px-[32px] py-5 sm:py-[24px]">
                        <p className="text-[16px] text-[#fff] font-Inter-Regular">
                            Digital assets to delegate
                        </p>
                        <ConfigProvider theme={{ algorithm: darkAlgorithm }}>
                            <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-3">
                                <TabBar tabBar={tabBar} setTabBar={setTabBar} stats={stats} />
                                <RangePicker
                                    className="w-full xl:w-[250px]"
                                    style={{ height: '48px', borderRadius: '12px' }}
                                    format={dateFormat}
                                    onChange={(values) => setRange(values)}
                                    defaultValue={null}
                                />
                            </div>
                        </ConfigProvider>
                    </div>
                </div>
                <TransactionHeader />
                {loading && (
                    <div className="px-4 sm:px-[20px] lg:px-[140px] text-gray1 py-8">Loading wills...</div>
                )}
                {!loading && error && (
                    <div className="px-4 sm:px-[20px] lg:px-[140px] text-red-400 py-8">{error}</div>
                )}
                {!loading && !error && items.length === 0 && (
                    <div className="flex flex-col px-4 sm:px-[20px] lg:px-[150px]"><NoData /></div>
                )}
                {!loading && !error && items.map((item) => (
                    <div key={item.id}>
                        <TransactionComponent item={item} />
                    </div>
                ))}
            </div>
            <DownloadWallet />
        </div>
    );
}
